import { memo, useEffect, useState } from "react"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"
import { z } from "zod"

import {
	Dialog,
	Form,
	FormInput,
	IconButton,
	SubmitButton,
	Tooltip,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { ColorPicker } from "@/components/color-picker"
import { toast } from "@/components/toast"
import { getProjectIcon } from "@/lib/projects"
import { mutateOk, useZero } from "@/lib/zero"

const EditChangelogTagFormSchema = z.object({
	name: z.string().min(1),
	icon: z.string().nullable(),
})

interface EditChangelogTagDialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	tag: { id: string; name: string; color: string; icon?: string | null } | null
}

export const EditChangelogTagDialog = memo(
	({ isOpen, onOpenChange, tag }: EditChangelogTagDialogProps) => {
		const zero = useZero()
		const [color, setColor] = useState(tag?.color ?? "#6B7280")

		const form = useZodForm({
			schema: EditChangelogTagFormSchema,
			defaultValues: {
				name: tag?.name ?? "",
				icon: tag?.icon ?? null,
			},
		})

		useEffect(() => {
			if (tag && isOpen) {
				form.reset({
					name: tag.name,
					icon: tag.icon ?? null,
				})
				setColor(tag.color)
			}
		}, [tag, isOpen, form])

		const iconWatch = form.watch("icon")

		if (!tag) return null

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				className="m-2 max-w-md p-0"
				title={<div className="p-6 pb-0">Edit changelog label</div>}
			>
				<Form
					id="edit-changelog-tag-form"
					form={form}
					className="flex flex-col"
					onSubmit={async (values) => {
						await mutateOk(
							zero.mutate(
								mutators.changelogTags.update({
									id: tag.id,
									color,
									icon: values.icon,
									name: values.name,
								}),
							),
						)
						onOpenChange(false)
						toast.success({ title: "Changelog label updated" })
					}}
				>
					<div className="flex items-center gap-2 px-6 pb-6 pt-2">
						<ColorPicker
							value={color}
							onChange={setColor}
							withIcon
							icon={iconWatch}
							onIconChange={(icon) => form.setValue("icon", icon)}
							withInputs
						>
							<button
								type="button"
								className="flex aspect-square h-8 w-8 items-center justify-center rounded-lg border"
							>
								{iconWatch ? (
									(() => {
										const Icon = getProjectIcon(iconWatch)
										return <Icon className="h-4 w-4" style={{ color }} />
									})()
								) : (
									<div
										className="h-3 w-3 rounded-full"
										style={{ backgroundColor: color }}
									/>
								)}
							</button>
						</ColorPicker>

						<FormInput
							textSize="xl"
							variant="borderless"
							name="name"
							placeholder="Label name"
						/>
						{iconWatch && (
							<Tooltip label="Remove icon">
								<IconButton
									className="text-xs text-tertiary hover:text-secondary"
									onClick={() => form.setValue("icon", null)}
									Icon={X}
									variant="ghost"
								/>
							</Tooltip>
						)}
					</div>

					<div className="flex items-center justify-end p-6">
						<SubmitButton form="edit-changelog-tag-form" disabled={false}>
							Save
						</SubmitButton>
					</div>
				</Form>
			</Dialog>
		)
	},
)
